import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CollectionHelper, CompareHelper } from '@barthauer/abc/core';
import {
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { AbcExpansionComponent, AbcTileMenuComponent, OnTileClickEvent } from '@barthauer/abc/ui-components';
import { CardsViewService } from './cards-view.service';
import { CardDefinition, CardModel, Order, VisibilityMode } from './viewModels/card.viewModel';

@Component({
  selector: 'abc-cards-view',
  templateUrl: './cards-view.component.html',
  styleUrls: ['./cards-view.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class CardsViewComponent implements OnInit {
  @Input() rtlEnabled: boolean;
  @Input() set groupsCard(data: CardModel) {
    this._groupsCard = data;
    this.groupingOfItems(this._groupsCard.CardDefinitions);
    let children = this._groupsCard?.CardDefinitions?.filter((child) => this.isVisibleOrder(child.Order));
    while (children?.length === 1 && children[0].CardDefinitions?.length) {
      const c = children[0];
      this.navigateTo(c);
      children = children[0].CardDefinitions?.filter((child) => this.isVisibleOrder(child.Order));
    }
  }
  @ViewChild('tilemenu', { static: true }) abcTileMenuComponent: AbcTileMenuComponent;
  @ViewChildren('abcExpansionPanel') abcExpansionPanel: QueryList<AbcExpansionComponent>;

  private _groupsCard: CardModel;
  private index: number = 0;
  private currentCardsView: CardDefinition[];
  public currentFolder;
  public activeIcon: boolean = false;
  public searchText: string;
  public displayArrowBack: boolean = false;
  public map = new Map<any, CardDefinition[]>();
  public historyDataMap = new Map<unknown, CardDefinition[]>();
  public tileNavMap = new Map<number, string>();
  public visibleCardDefinitions: CardDefinition[];
  public searchMap = new Map<string, CardDefinition[]>();
  public tileMenuSize: number;
  public tileSize: number;
  public res = ResourceManager.getResources('abc-cards-view');

  constructor(
    public iconRegistry: MatIconRegistry,
    public sanitizer: DomSanitizer,
    private persistenceService: PersistenceService,
    private messageBoxService: MessageBoxService,
    private cardsViewService: CardsViewService,
    public breakpointObserver: BreakpointObserver,
  ) {
    iconRegistry.addSvgIcon('arrowBack', sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_back.svg'));
    iconRegistry.addSvgIcon('homeIcon', sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_home.svg'));

    iconRegistry.addSvgIcon('Edit', sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_document.svg'));
    iconRegistry.addSvgIcon('Execute', sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_button.svg'));
    iconRegistry.addSvgIcon('Select', sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_select.svg'));
    iconRegistry.addSvgIcon(
      'moreOptions',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_moreDiractoryHorizontal.svg'),
    );
    iconRegistry.addSvgIcon('Export', sanitizer.bypassSecurityTrustResourceUrl('assets/images/File_Export.svg'));
    iconRegistry.addSvgIcon('Import', sanitizer.bypassSecurityTrustResourceUrl('assets/images/File_Import.svg'));
    iconRegistry.addSvgIcon(
      'deleteCard',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_deleteCard.svg'),
    );
    iconRegistry.addSvgIcon(
      'breadcrumb',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_breadcrumb.svg'),
    );
    iconRegistry.addSvgIcon(
      'breadcrumbActive',
      sanitizer.bypassSecurityTrustResourceUrl('assets/images/KAOS_arena_button.svg'),
    );
    iconRegistry.addSvgIcon('no-data-icon', sanitizer.bypassSecurityTrustResourceUrl('assets/images/No-items.svg'));
    iconRegistry.addSvgIcon('search', sanitizer.bypassSecurityTrustResourceUrl('assets/images/search.svg'));
    iconRegistry.addSvgIcon('clear', sanitizer.bypassSecurityTrustResourceUrl('assets/images/clear-search.svg'));
  }
  async ngOnInit(): Promise<void> {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
      .subscribe((result) => {
        if (result.breakpoints[Breakpoints.XSmall]) {
          this.tileMenuSize = 2;
          this.tileSize = 3;
        }
        if (result.breakpoints[Breakpoints.Small]) {
          this.tileMenuSize = 2;
          this.tileSize = 3;
        }
        if (result.breakpoints[Breakpoints.Medium]) {
          this.tileMenuSize = 3;
          this.tileSize = 4;
        }
        if (result.breakpoints[Breakpoints.Large]) {
          this.tileMenuSize = 4;
          this.tileSize = 5;
        }
        if (result.breakpoints[Breakpoints.XLarge]) {
          this.tileMenuSize = 4;
          this.tileSize = 5;
        }
      });
    setTimeout(() => {
      document.querySelector('.dx-accordion-item-title-caption')?.classList.add('hideArrowBack');
    }, 0);
  }

  public navigateTo(obj: CardDefinition): void {
    if (!CompareHelper.isEmptyObject(obj.CardDefinitions)) {
      this.currentCardsView = obj.CardDefinitions;
      this.currentFolder = obj.DisplayText;
      this.tileNavMap.set(this.index, obj.DisplayText);
      this.groupingOfItems(obj.CardDefinitions);
      this.cardsViewService.documentPath = obj.DisplayText;
      this.displayArrowBack = true;
    }
  }

  public primaryActionClick(obj: CardDefinition): void {
    obj.PrimaryAction?.Action.call(this, obj);
  }

  public thirdActionClick(obj: CardDefinition): void {
    obj.ThirdAction.Action.call(this, obj);
  }

  public fourthActionClick(obj: CardDefinition): void {
    obj.FourthAction.Action.call(this, obj);
  }

  public secondaryActionClick(obj: CardDefinition): void {
    if (obj.SecondaryAction?.Action) {
      obj.SecondaryAction.Action.call(this, obj);
    } else {
      const settings = new MessageSettings();
      settings.mode = MessageMode.Info;
      settings.icon = MessageBoxIcon.Info;
      settings.button = MessageBoxButtons.YesNo;
      settings.message = this.res('ConfirmDeleteMessage');
      this.messageBoxService.showMessageBox(settings).subscribe((result: DialogResult) => {
        if (DialogResult.Yes === result['result']) {
          const cardDeletePersistQuery: TargetObjectData = new TargetObjectData();
          cardDeletePersistQuery.Mode = PersistMode.Delete;
          cardDeletePersistQuery.ObjectKey = new ObjectKey();
          cardDeletePersistQuery.ObjectKey.Id = obj.DataObject.Id;
          cardDeletePersistQuery.ObjectKey.ObjectType = 'MElement';

          const pom: PersistObjectModel = new PersistObjectModel();
          pom.ObjectSpaceName = 'KAOS.Master';
          pom.Object = cardDeletePersistQuery;
          const documentsList = this.map.values().next(null).value;
          documentsList.forEach((item, index, object) => {
            if (item.DisplayText === obj.DataObject.Designation) {
              object.splice(index, 1);
            }
          });

          this.persistenceService.executePersistObjectQuery(pom).subscribe();
          this.messageBoxService.dialog.closeAll();
        } else {
          this.messageBoxService.dialog.closeAll();
        }
      });
    }
  }

  private groupingOfItems(data: CardDefinition[]): void {
    this.historyDataMap.set(this.index, data);
    this.index++;
    this.createMappingData(data);
  }

  public prevAction(): void {
    this.index--;
    if (this.index === 1) this.hideElementsFromCardView();
    this.historyDataMap.forEach((value: CardDefinition[], key: number) => {
      if (key === this.historyDataMap.size - 2) {
        this.historyDataMap.delete(key + 1);
        this.tileNavMap.delete(key + 1);
        this.createMappingData(value);
      }
    });
  }
  public onTileClick(event: OnTileClickEvent): void {
    this.searchText = '';
    this.createMappingData(this.historyDataMap.get(event.data.key));
    const sizeMap = this.historyDataMap.size;
    for (let index = event.data.key + 1; index <= sizeMap; index++) {
      this.historyDataMap.delete(index);
      this.tileNavMap.delete(index);
    }
    this.index = this.historyDataMap.size;
  }

  private createMappingData(data: CardDefinition[]): void {
    if (data) {
      this.map.clear();
      data.map((element) => {
        if (!this.map.has(element.Order.Number)) {
          this.map.set(
            element.Order.Number,
            data.filter((item) => item.Order.Number === element.Order.Number),
          );
        }
      });
    }
  }

  public returnHomePage(): void {
    this.searchText = '';
    this.tileNavMap.clear();
    this.map.clear();
    this.map.set(0, this.historyDataMap.get(0));
    this.historyDataMap = new Map(this.map);
    this.index = 1;
    this.hideElementsFromCardView();
  }

  public applyFilter(filterValue: string): void {
    this.displayArrowBack = false;
    this.visibleCardDefinitions = [];
    if (filterValue) {
      if (this.index !== 1) {
        this.isVisible(filterValue.toLocaleLowerCase(), this.currentCardsView, this.visibleCardDefinitions);
      } else {
        this.isVisible(filterValue.toLocaleLowerCase(), this.historyDataMap.get(0), this.visibleCardDefinitions);
        this.currentFolder = this.historyDataMap.get(0)[0].DisplayText;
      }

      this.map.clear();
      this.visibleCardDefinitions.map((element) => {
        if (!this.map.has(element.Order.Number)) {
          this.map.set(
            element.Order.Number,
            this.visibleCardDefinitions.filter((item) => item.Order.Number === element.Order.Number),
          );
        }
      });

      const cardsArray = Array.from(this.map.values());
      const folders: string[] = [];
      for (const arr of cardsArray) {
        this.sortFilteredCards(arr);
        for (const g of this._groupsCard.CardDefinitions) {
          if (this.containsAnyCardDefinition(g, arr)) {
            folders.push(g.DisplayText);
          }
        }
      }
      this.currentFolder = CollectionHelper.distinctValues(folders).join(' | ');
    } else {
      this.backToCurrentPathAfterSearch();
    }
  }

  private containsAnyCardDefinition(parent: CardDefinition, children: CardDefinition[]): boolean {
    if (!parent || !children?.length) {
      return false;
    }
    for (var c of children) {
      if (parent === c) {
        return true;
      } else if (parent.CardDefinitions) {
        for (var p of parent.CardDefinitions) {
          if (this.containsAnyCardDefinition(p, [c])) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private isVisible(search: string, cardDefinitions: CardDefinition[], visibleCardDefinitions: CardDefinition[]): void {
    if (cardDefinitions) {
      cardDefinitions.forEach((cardDefinition) => {
        if (cardDefinition.IsVisible(search) && cardDefinition.DisplayText.toLocaleLowerCase().includes(search)) {
          const nodeTree = this.getnodeTreeArray(this.historyDataMap.get(0)[0], cardDefinition.DataObject.Id);
          const fullPath = this.getFullPath(nodeTree);
          cardDefinition.SecondaryDisplayText = fullPath;
          visibleCardDefinitions.push(cardDefinition);
        }

        this.isVisible(search, cardDefinition.CardDefinitions, visibleCardDefinitions);
      });
    }
  }

  private getnodeTreeArray(node: CardDefinition, documentId: string): CardDefinition[] {
    if (node.DataObject.Id === documentId) {
      return [];
    }

    if (node.CardDefinitions) {
      for (const nodeTree of node.CardDefinitions) {
        const result = this.getnodeTreeArray(nodeTree, documentId);
        if (result) {
          return [nodeTree].concat(result);
        }
      }
    }
    return [];
  }

  private getFullPath(nodeTree: CardDefinition[]): string {
    return nodeTree?.map((_) => _.DisplayText).join(' > ');
  }

  private sortFilteredCards(array: CardDefinition[] | undefined): CardDefinition[] {
    if (!array) {
      return [];
    }

    return array.sort((a, b) => (a.DisplayText.toLowerCase() < b.DisplayText.toLowerCase() ? -1 : 1));
  }

  private backToCurrentPathAfterSearch(): void {
    if (this.index !== 1) {
      this.createMappingData(this.currentCardsView);
      this.displayArrowBack = true;
    } else {
      this.currentFolder = this.historyDataMap.get(0)[0].DisplayText;
      this.returnHomePage();
    }
  }

  public trackByValue(index, item): string {
    return item.value;
  }

  clearSearchInput(): void {
    this.searchText = '';
    this.backToCurrentPathAfterSearch();
  }

  openExpansion(e, index) {
    this.abcExpansionPanel.toArray().forEach((_, _index) => {
      if (index !== _index) this.abcExpansionPanel.toArray()[_index].selectedIndex = -1;
      else this.abcExpansionPanel.toArray()[index].selectedIndex = 0;
    });
  }

  isVisibleOrder(order: Order): boolean {
    return order?.VisibilityMode === VisibilityMode.Default;
  }

  private hideElementsFromCardView() {
    this.displayArrowBack = false;
    this.activeIcon = false;
    setTimeout(() => {
      document.querySelector('.dx-accordion-item-title-caption').classList.add('hideArrowBack');
    }, 0);
  }
}
